import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaExternalLinkAlt, FaDesktop, FaWarehouse, FaDatabase } from "react-icons/fa";
import { useInView } from "react-intersection-observer";
// Import the GitHub media utilities
import { getGitHubCdnUrl, getImageUrl } from "../../utils/githubMediaUtils";

// Define image filenames for GitHub
const IMAGE_FILENAMES = {
  aiora: "aiora.png",
  albasala: "albasala.png",
  rozhi: "rozhi.png",
  atelierhuna: "atelierhuna.png",
  rozhisystem: "rozhisystem.png",
  hamishagullsystem: "hamishagullsystem.png",
  dizhwarsystem: "dizhwarsystem.png"
};

const OurWorks = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState("websites");
  const [projects, setProjects] = useState([]);
  const [animateCards, setAnimateCards] = useState(false);
  const [languageKey, setLanguageKey] = useState(i18n.language); // Track language changes
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });
  
  // Update language key when language changes
  useEffect(() => {
    setLanguageKey(i18n.language);
  }, [i18n.language]);

  // Define project data keys - not using t() here to avoid translation issues
  const getWebsiteProjects = () => [
    {
      id: 1,
      nameKey: "project_albasala",
      descriptionKey: "news_agency_website_desc",
      image: getImageUrl("works", IMAGE_FILENAMES.albasala),
      url: "https://albasala.com",
      technologies: ["React", "Node.js", "MongoDB"],
      type: "website"
    },
    {
      id: 2,
      nameKey: "project_aiora",
      descriptionKey: "architecture_portfolio_desc",
      image: getImageUrl("works", IMAGE_FILENAMES.aiora),
      url: "https://aioraarchitect.com",
      technologies: ["React", "Tailwind CSS", "Framer Motion"],
      type: "website"
    },
    {
      id: 3,
      nameKey: "project_huna",
      descriptionKey: "ecommerce_website_desc",
      image: getImageUrl("works", IMAGE_FILENAMES.atelierhuna),
      url: "https://atelierhuna.com",
      technologies: ["React", "Redux", "Express", "MongoDB"],
      type: "website"
    },
    {
      id: 4,
      nameKey: "project_rozhi",
      descriptionKey: "corporate_website_desc",
      image: getImageUrl("works", IMAGE_FILENAMES.rozhi),
      url: "https://rozhiiraq.com",
      technologies: ["React", "Node.js", "MySQL"],
      type: "website"
    }
  ];

  const getSystemProjects = () => [
    {
      id: 5,
      nameKey: "project_rozhi_wms",
      descriptionKey: "rozhi_wms_desc",
      image: getImageUrl("works", IMAGE_FILENAMES.rozhisystem),
      technologies: ["React", "Node.js", "Express", "MongoDB"],
      featuresKeys: ["inventory_tracking", "order_management", "reporting"],
      type: "system"
    },
    {
      id: 6,
      nameKey: "project_hamisha_wms",
      descriptionKey: "hamisha_wms_desc",
      image: getImageUrl("works", IMAGE_FILENAMES.hamishagullsystem),
      technologies: ["React", "Redux", "Express", "PostgreSQL"],
      featuresKeys: ["barcode_scanning", "realtime_analytics", "multi_warehouse"],
      type: "system"
    },
    {
      id: 7,
      nameKey: "project_dizhwar_wms",
      descriptionKey: "dizhwar_wms_desc",
      image: getImageUrl("works", IMAGE_FILENAMES.dizhwarsystem),
      technologies: ["React", "Node.js", "GraphQL", "MongoDB"],
      featuresKeys: ["supplier_management", "demand_forecasting", "mobile_app"],
      type: "system"
    }
  ];
  
  // Initialize projects on component mount and when language changes
  useEffect(() => {
    // Get fresh project data with the current language
    const websiteProjectsData = getWebsiteProjects();
    console.log('Initializing projects with websiteProjects:', websiteProjectsData);
    setProjects(websiteProjectsData);
    // Force animation to be visible
    setAnimateCards(true);
    controls.start("visible");
  }, [t, controls]);
  
  // Update projects when tab changes
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    
    console.log('Tab changed to:', tab);
    setActiveTab(tab);
    
    // Immediately update projects without delay based on selected tab
    const newProjects = tab === "websites" ? getWebsiteProjects() : getSystemProjects();
    console.log('Setting new projects:', newProjects);
    setProjects(newProjects);
  };
  
  // Update projects when language changes
  useEffect(() => {
    // Force update when language changes
    console.log('Language changed to:', i18n.language);
    
    // Debug translations
    console.log('Translation test - project_albasala:', t('project_albasala'));
    console.log('Translation test - project_aiora:', t('project_aiora'));
    console.log('Translation test - project_rozhi_wms:', t('project_rozhi_wms'));
    
    // Update projects with new language
    const currentProjects = activeTab === "websites" ? getWebsiteProjects() : getSystemProjects();
    setProjects([]);
    // Small delay to ensure DOM updates
    setTimeout(() => {
      setProjects(currentProjects);
    }, 50);
  }, [i18n.language, activeTab, t]);

  // Always ensure animation is enabled when component is in view
  useEffect(() => {
    if (inView) {
      setAnimateCards(true);
      controls.start("visible");
    }
  }, [controls, inView]);
  
  // Debug log to check projects state
  useEffect(() => {
    console.log('Current projects state:', projects);
  }, [projects]);

  // Simplified animation variants for better performance and reliability
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2
      }
    }
  };

  const projectVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2
      }
    }
  };

  // Simplified tab animations for better performance
  const tabVariants = {
    active: {
      color: "#000000",
      transition: { duration: 0.2 }
    },
    inactive: {
      color: "#FFFFFF",
      transition: { duration: 0.2 }
    }
  };

  // Simplified floating animation for better performance
  const floatingAnimation = {
    y: [0, -5, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div id="our-works" className="bg-gradient-to-b from-gray-900 to-black py-20" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          className="text-center mb-12 px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-4xl font-bold mb-4">
            {t("our_works_title")} <span className="text-yellow-400">{t("our_works_highlight")}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {t("our_works_description")}
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <motion.div 
            className="bg-gray-800 p-1 rounded-lg flex"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <motion.button
              className={`px-6 py-3 rounded-lg ${activeTab === "websites" ? "bg-yellow-500 text-black" : "text-white"}`}
              onClick={() => handleTabChange("websites")}
              variants={tabVariants}
              animate={activeTab === "websites" ? "active" : "inactive"}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ 
                backgroundColor: activeTab === "websites" ? "#EAB308" : "transparent"
              }}
            >
              <div className="flex items-center">
                <FaDesktop className={isRTL ? "ml-2" : "mr-2"} />
                {t("websites_tab")}
              </div>
            </motion.button>
            <motion.button
              className={`px-6 py-3 rounded-lg ${activeTab === "systems" ? "bg-yellow-500 text-black" : "text-white"}`}
              onClick={() => handleTabChange("systems")}
              variants={tabVariants}
              animate={activeTab === "systems" ? "active" : "inactive"}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ 
                backgroundColor: activeTab === "systems" ? "#EAB308" : "transparent"
              }}
            >
              <div className="flex items-center">
                <FaWarehouse className={isRTL ? "ml-2" : "mr-2"} />
                {t("systems_tab")}
              </div>
            </motion.button>
          </motion.div>
        </div>

        {/* Projects Grid */}
        <motion.div 
          key={activeTab}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8"
          variants={containerVariants}
          initial="visible"
          animate="visible"
        >
          {projects && projects.length > 0 ? projects.map((project) => (
            <motion.div
              key={`${project.id}-${i18n.language}`}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="bg-gray-800 rounded-xl overflow-hidden shadow-lg transform transition-all duration-200 hover:shadow-yellow-500/20 hover:shadow-xl h-full flex flex-col"
            >
              <div className="h-40 sm:h-48 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
                <img 
                  src={project.image} 
                  alt={t(project.nameKey)} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = getImageUrl("", "placeholder.jpg");
                  }}
                />
                {project.type === "website" && (
                  <motion.a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-4 right-4 bg-yellow-500 text-black p-2 rounded-full z-20"
                    whileHover={{ scale: 1.2, rotate: 15 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaExternalLinkAlt />
                  </motion.a>
                )}
              </div>
              <div className="p-4 sm:p-6 flex-grow flex flex-col">
                {/* Debug output */}
                {console.log('Project name key:', project.nameKey, 'Current language:', i18n.language, 'Translation:', t(project.nameKey))}
                <h3 className="text-lg sm:text-xl font-bold mb-2 text-yellow-400">{t(project.nameKey)}</h3>
                <p className="text-gray-300 mb-4 text-sm sm:text-base">{t(project.descriptionKey)}</p>
                
                {/* Technologies */}
                <div className="mb-4 mt-auto">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-400 mb-2">{t("technologies_label")}:</h4>
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {project.technologies.map((tech, index) => (
                      <span 
                        key={index} 
                        className="text-xs bg-gray-700 text-yellow-400 px-2 py-1 rounded"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Features for systems */}
                {project.type === "system" && project.featuresKeys && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">{t("key_features_label")}:</h4>
                    <ul className="list-disc list-inside text-gray-300 text-sm">
                      {project.featuresKeys.map((featureKey, index) => (
                        <li key={index}>{t(featureKey)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          )) : (
            <div className="col-span-full text-center py-10">
              <p className="text-gray-400">{t("no_projects_available")}</p>
            </div>
          )}
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <motion.p 
            className="text-xl text-gray-300 mb-6"
            whileInView={{ 
              scale: [1, 1.05, 1],
              transition: { duration: 2, repeat: Infinity }
            }}
          >
            {t("cta_question")}
          </motion.p>
          <motion.a
            href="#software-contact"
            className="inline-block bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg shadow-lg"
            whileHover={{ scale: 1.05, boxShadow: "0 0 15px rgba(255, 215, 0, 0.7)" }}
            whileTap={{ scale: 0.95 }}
          >
            {t("start_project_button")}
          </motion.a>
        </motion.div>
      </div>
    </div>
  );
};

export default OurWorks;
